<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import { computeAvatarUrl } from '~~/utils/computeAvatarUrl'

defineProps<{
  collapsed?: boolean
}>()

const colorMode = useColorMode()
const appConfig = useAppConfig()
const auth = useAuth()

const colors = [
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
]
const neutrals = ['slate', 'gray', 'zinc', 'neutral', 'stone']

const items = computed<DropdownMenuItem[][]>(() => [
  [
    {
      type: 'label',
      label: auth.user?.name,
      avatar: {
        src: computeAvatarUrl(auth.user?.name ?? ''),
        alt: auth.user?.name,
      },
    },
  ],
  [
    {
      label: 'Profil',
      icon: 'i-lucide-user',
    },
    {
      label: 'Facturation',
      icon: 'i-lucide-credit-card',
    },
    {
      label: 'Paramètres',
      icon: 'i-lucide-settings',
      to: '/app/settings',
    },
  ],
  [
    {
      label: 'Thème',
      icon: 'i-lucide-palette',
      children: [
        {
          label: 'Primaire',
          slot: 'chip',
          chip: appConfig.ui.colors.primary,
          content: {
            align: 'center',
            collisionPadding: 16,
          },
          children: colors.map((color) => ({
            label: {
              red: 'Rouge',
              orange: 'Orange',
              amber: 'Ambre',
              yellow: 'Jaune',
              lime: 'Lime',
              green: 'Vert',
              emerald: 'Émeraude',
              teal: 'Turquoise',
              cyan: 'Cyan',
              sky: 'Ciel',
              blue: 'Bleu',
              indigo: 'Indigo',
              violet: 'Violet',
              purple: 'Pourpre',
              fuchsia: 'Fuchsia',
              pink: 'Rose',
              rose: 'Rose'
            }[color] || color,
            chip: color,
            slot: 'chip',
            checked: appConfig.ui.colors.primary === color,
            type: 'checkbox',
            onSelect: (e) => {
              e.preventDefault()

              appConfig.ui.colors.primary = color
            },
          })),
        },
        {
          label: 'Neutre',
          slot: 'chip',
          chip:
            appConfig.ui.colors.neutral === 'neutral'
              ? 'old-neutral'
              : appConfig.ui.colors.neutral,
          content: {
            align: 'end',
            collisionPadding: 16,
          },
          children: neutrals.map((color) => ({
            label: {
              slate: 'Ardoise',
              gray: 'Gris',
              zinc: 'Zinc',
              neutral: 'Neutre',
              stone: 'Pierre'
            }[color] || color,
            chip: color === 'neutral' ? 'old-neutral' : color,
            slot: 'chip',
            type: 'checkbox',
            checked: appConfig.ui.colors.neutral === color,
            onSelect: (e) => {
              e.preventDefault()

              appConfig.ui.colors.neutral = color
            },
          })),
        },
      ],
    },
    {
      label: 'Apparence',
      icon: 'i-lucide-sun-moon',
      children: [
        {
          label: 'Clair',
          icon: 'i-lucide-sun',
          type: 'checkbox',
          checked: colorMode.value === 'light',
          onSelect(e: Event) {
            e.preventDefault()

            colorMode.preference = 'light'
          },
        },
        {
          label: 'Sombre',
          icon: 'i-lucide-moon',
          type: 'checkbox',
          checked: colorMode.value === 'dark',
          onUpdateChecked(checked: boolean) {
            if (checked) {
              colorMode.preference = 'dark'
            }
          },
          onSelect(e: Event) {
            e.preventDefault()
          },
        },
      ],
    },
  ],
])
</script>

<template>
  <UDropdownMenu
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{
      content: collapsed ? 'w-48' : 'w-(--reka-dropdown-menu-trigger-width)',
    }"
  >
    <UButton
      v-bind="{
        ...auth.user,
        avatar: {
          src: computeAvatarUrl(auth.user?.name ?? ''),
          alt: auth.user?.name,
        },
        label: collapsed ? undefined : auth.user?.name,
        trailingIcon: collapsed ? undefined : 'i-lucide-chevrons-up-down',
      }"
      color="neutral"
      variant="ghost"
      block
      :square="collapsed"
      class="data-[state=open]:bg-elevated"
      :ui="{
        trailingIcon: 'text-dimmed',
      }"
    />

    <template #chip-leading="{ item }">
      <span
        :style="{
          '--chip-light': `var(--color-${(item as any).chip}-500)`,
          '--chip-dark': `var(--color-${(item as any).chip}-400)`,
        }"
        class="ms-0.5 size-2 rounded-full bg-(--chip-light) dark:bg-(--chip-dark)"
      />
    </template>
  </UDropdownMenu>
</template>
